import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore, setLogLevel } from 'firebase/firestore';

// Silence Firestore internal log noise (e.g. backoff delay, internal network retries)
try {
  setLogLevel('silent');
} catch (e) {
  // ignore
}

// Intercept Firestore internal error logs so quota limits don't register as fatal app crashes
if (typeof window !== 'undefined') {
  const origConsoleError = console.error;
  const extractErrorText = (a: any): string => {
    if (a == null) return '';
    if (typeof a === 'string') return a;
    let out = '';
    if (a instanceof Error || (typeof a === 'object' && a !== null)) {
      if (a.name) out += ' ' + a.name;
      if (a.code) out += ' ' + a.code;
      if (a.message) out += ' ' + a.message;
      if (a.stack) out += ' ' + a.stack;
      try {
        const s = String(a);
        if (s !== '[object Object]') out += ' ' + s;
      } catch (_) {}
      try {
        out += ' ' + JSON.stringify(a);
      } catch (_) {}
    }
    return out || String(a);
  };

  console.error = (...args: any[]) => {
    const combined = args.map(extractErrorText).join(' ').toLowerCase();
    if (
      combined.includes('resource-exhausted') || 
      combined.includes('maximum backoff delay') ||
      combined.includes('quota limit exceeded') ||
      combined.includes('quota exceeded') ||
      combined.includes('free daily write units') ||
      combined.includes('unsupported color function')
    ) {
      console.warn('[Firestore / Resource Notice]', ...args);
      try {
        window.sessionStorage?.setItem('firestore_quota_exceeded', 'true');
        window.localStorage?.setItem('firestore_quota_exceeded', 'true');
        window.dispatchEvent(new CustomEvent('firestore-quota-exceeded'));
      } catch (_) {}
      return;
    }
    origConsoleError.apply(console, args);
  };
}

// Remove direct JSON import to avoid potential ESM/Vite resolution issues at runtime
// Instead, we will rely on the hardcoded config which is kept in sync
// import firebaseConfigData from '../firebase-applet-config.json';

// Hardcoded configuration to ensure data persistence across shared links
const hardcodedConfig = {
  projectId: "almajd2026-e9349",
  appId: "1:783511391423:web:85399e924140c969e24a0a",
  apiKey: "AIzaSyD2pYSzJtgDk3Bo3iUhLLtiEPrUNr2PFmg",
  authDomain: "almajd2026-e9349.firebaseapp.com",
  firestoreDatabaseId: "ai-studio-58c688ba-cef7-45be-84cf-c493b48e9a42",
  storageBucket: "almajd2026-e9349.firebasestorage.app",
  messagingSenderId: "783511391423"
};

export const firebaseConfig = {
  apiKey: hardcodedConfig.apiKey,
  authDomain: hardcodedConfig.authDomain,
  projectId: hardcodedConfig.projectId,
  storageBucket: hardcodedConfig.storageBucket,
  messagingSenderId: hardcodedConfig.messagingSenderId,
  appId: hardcodedConfig.appId,
};

console.log("Initializing Firebase with hardcoded config...");
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
console.log("Firebase App initialized.");

const dbId = hardcodedConfig.firestoreDatabaseId;
let db: any;
try {
  console.log(`Initializing Firestore (DB ID: ${dbId || 'default'})...`);
  db = dbId && dbId !== '(default)'
    ? getFirestore(app, dbId)
    : getFirestore(app);
  console.log("Firestore initialized successfully.");
} catch (e) {
  console.warn("Firestore initialization with specific ID failed, falling back to default:", e);
  try {
    db = getFirestore(app);
    console.log("Firestore initialized with default fallback.");
  } catch (e2) {
    console.error("Critical: Firestore initialization failed completely:", e2);
    // last resort: a proxy that logs everything but doesn't crash on define
    db = new Proxy({}, { 
      get: (target, prop) => { 
        console.error(`Attempted to access Firestore property "${String(prop)}" but Firestore failed to initialize.`);
        return () => {}; 
      } 
    });
  }
}

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId: string | undefined;
    email: string | null | undefined;
    emailVerified: boolean | undefined;
    isAnonymous: boolean | undefined;
    tenantId: string | null | undefined;
    providerInfo: {
      providerId: string;
      displayName: string | null;
      email: string | null;
      photoUrl: string | null;
    }[];
  }
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  if (isQuotaError(error)) {
    console.warn(`[Firestore Quota] Operation "${operationType}" on "${path}" paused due to free tier limits.`);
    return;
  }
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errInfo: FirestoreErrorInfo = {
    error: errorMessage,
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData?.map((provider: any) => ({
        providerId: provider.providerId,
        displayName: provider.displayName,
        email: provider.email,
        photoUrl: provider.photoURL
      })) || []
    },
    operationType,
    path
  }
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

let lastQuotaEventTime = 0;

export function isQuotaError(error: any): boolean {
  if (!error) return false;
  const msg = error?.message || String(error);
  const code = error?.code || '';
  const isQuota = 
    code === 'resource-exhausted' ||
    msg.includes('resource-exhausted') ||
    msg.includes('Quota exceeded') || 
    msg.includes('quota metric') || 
    msg.includes('Quota limit exceeded') ||
    msg.includes('Free daily write units') ||
    msg.includes('free tier database');
  
  if (isQuota && typeof window !== 'undefined') {
    const now = Date.now();
    if (now - lastQuotaEventTime > 5000) {
      lastQuotaEventTime = now;
      window.dispatchEvent(new CustomEvent('firestore-quota-exceeded'));
    }
  }
  
  return Boolean(isQuota);
}

export function isUnavailableOrOfflineError(error: any): boolean {
  if (!error) return false;
  const msg = error?.message || String(error);
  const code = error?.code || '';
  return code === 'unavailable' ||
         code === 'failed-precondition' ||
         msg.includes('Could not reach Cloud Firestore backend') ||
         msg.includes('Connection failed') ||
         msg.includes('offline mode') ||
         msg.includes('The operation could not be completed') ||
         msg.includes('[code=unavailable]') ||
         msg.includes('network-request-failed');
}

export { auth, db };
